import {
  ApplicationConfig,
  provideZoneChangeDetection,
  provideAppInitializer,
  inject,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, HttpClient } from '@angular/common/http';

import { Amplify } from 'aws-amplify';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import CapturePreset from './preset/preset';

import {
  APP_CONFIG,
  AppConfig,
  getConfiguration,
  setConfiguration,
} from './configuration/app-configuration';

import {
  TranslateLoader,
  TranslateModule,
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateService,
} from '@ngx-translate/core';
import { HttpLoaderFactory } from './shared/translate-loader/translate-loader.service';
import { firstValueFrom } from 'rxjs';

const APP_URL = new URL(window.location.href);
const APP_DOMAIN = `${APP_URL.origin}`;

export class FallbackMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    console.warn(`Missing translation key: ${params.key}`);
    return params.key;
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(),

    provideAppInitializer(() =>
      fetch(`${APP_DOMAIN}/config.json`)
        .then(response => response.json())
        .then((config: AppConfig) => {
          setConfiguration(config);
          Amplify.configure({
            Auth: {
              Cognito: {
                userPoolId: config.auth.userPoolId,
                userPoolClientId: config.auth.userPoolClientId,
                loginWith: {
                  oauth: {
                    domain: config.auth.domain,
                    scopes: ['openid', 'email'],
                    redirectSignIn: [`${APP_DOMAIN}/main`],
                    redirectSignOut: [`${APP_DOMAIN}/login`],
                    responseType: 'token',
                  },
                },
              },
            },
          });
        }),
    ),

    {
      provide: APP_CONFIG,
      useFactory: getConfiguration,
    },

    providePrimeNG({
      theme: {
        preset: CapturePreset,
      },
    }),

    ...TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: FallbackMissingTranslationHandler,
      },
    }).providers!,

    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      translate.addLangs(['en', 'fi']);
      translate.setDefaultLang('en');
      return firstValueFrom(translate.use('en'));
    }),

    provideAnimationsAsync(),
  ],
};
